/**
 * TaskAttachmentsDialog — Manage document attachments for a Macro-Schedule
 * activity (schedule_tasks). Shows currently attached documents and lets
 * the user open the DocumentPicker to add more.
 *
 * Props:
 *  - open, onClose
 *  - projectId
 *  - task: { id, name }
 *  - onChanged?()   — called after add/remove so the parent can refresh counts
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileText, Trash2, Plus, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import DocumentPickerDialog from '@/components/dms/DocumentPickerDialog';

export default function TaskAttachmentsDialog({ open, onClose, projectId, dmsProjectId, task, onChanged }) {
  const dmsPid = dmsProjectId || projectId;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const load = useCallback(async () => {
    if (!task?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/scheduling/projects/${projectId}/tasks/${task.id}/attachments`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load attachments');
    } finally { setLoading(false); }
  }, [projectId, task?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const attach = async (docs) => {
    try {
      await api.post(`/scheduling/projects/${projectId}/tasks/${task.id}/attachments`, {
        doc_ids: docs.map((d) => d.id),
      });
      toast.success(`Attached ${docs.length} document(s)`);
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Attach failed');
    }
  };

  const detach = async (docId) => {
    try {
      await api.delete(`/scheduling/projects/${projectId}/tasks/${task.id}/attachments/${docId}`);
      toast.success('Detached');
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Detach failed');
    }
  };

  const download = async (link) => {
    const doc = link.document;
    if (!doc?.file_url) { toast.info('No file attached to this document'); return; }
    try {
      const path = doc.file_url.replace(/^\/api/, '');
      const r = await api.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.file_name || `${doc.doc_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="max-w-2xl bg-white dark:bg-slate-900" data-testid="task-attachments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-sky-500" />
              Documents attached to <span className="font-mono text-sm text-slate-500">{task?.name || task?.id?.slice(0, 8)}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="border border-slate-200 dark:border-slate-700/50 rounded-md overflow-hidden">
            <div className="px-2 py-1 text-[10px] uppercase text-slate-500 border-b border-slate-200 dark:border-slate-700/50 flex justify-between">
              <span>{items.length} attached</span>
              <Button size="sm" variant="ghost" onClick={() => setShowPicker(true)} className="h-6 text-xs" data-testid="task-att-attach-more">
                <Plus className="w-3 h-3 mr-1" /> Attach more
              </Button>
            </div>
            <div className="max-h-[380px] overflow-auto">
              {loading ? (
                <div className="p-4 text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
              ) : items.length === 0 ? (
                <div className="p-6 text-xs text-slate-500 text-center">No documents attached to this activity yet. Click <b>Attach more</b> to pick from DMS.</div>
              ) : items.map((it) => {
                const d = it.document || {};
                return (
                  <div key={it.link_id} className="text-xs px-2 py-1.5 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700/30" data-testid={`task-att-row-${d.doc_number}`}>
                    <FileText className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
                    <span className="font-mono text-slate-500 w-28 truncate">{d.doc_number}</span>
                    <span className="flex-1 truncate text-slate-800 dark:text-slate-100">{d.title}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">{d.discipline}</span>
                    <span className="text-[10px] font-mono text-slate-400">Rev {d.revision}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => download(it)} title="Download" data-testid={`task-att-dl-${d.doc_number}`}>
                      <Download className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 px-1.5 text-red-500 hover:text-red-600" onClick={() => detach(d.id)} title="Detach" data-testid={`task-att-rm-${d.doc_number}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentPickerDialog
        open={showPicker}
        projectId={dmsPid}
        onClose={() => setShowPicker(false)}
        onPick={attach}
        title={`Attach documents to ${task?.name || 'activity'}`}
      />
    </>
  );
}
